import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Editor } from 'primereact/editor';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Icon,
  SimpleGrid,
  Stack,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { FaFacebook, FaInstagram } from 'react-icons/fa';
import { IoCloseCircleOutline } from 'react-icons/io5';
import { BsFillTelephoneFill } from 'react-icons/bs';
import { ImFilePdf } from "react-icons/im";
import { TbWorldWww } from "react-icons/tb";
import { FiMapPin } from 'react-icons/fi';
import { useDiscount } from 'contexts/DiscountContext';
import { isValidUrl } from 'helpers/helper';
import CInput from 'components/CInput';
import CButton from 'components/CButton';
import CRadio from 'components/CRadio';
import ImageUpload from 'components/ImageUpload';
import CSingleFileUpload from 'components/CSingleFileUpload';
import DiscountCategoryDropdown from 'components/DiscountCategoryDropdown';

const discountOptions = [
  { value: "single", label: "Single" },
  { value: "monthly", label: "Monthly" },
  { value: "multiple", label: "Multiple" },
];

const discountModeOptions = [
  { value: "percentage", label: "Percentage" },
  { value: "amount", label: "Amount" },
];

export default function NewDiscount() {
  const { getSingleDiscount, createDiscount, updateDiscount, } = useDiscount();
  const navigate = useNavigate();
  const params = useParams();

  const textColor = useColorModeValue('secondaryGray.900', 'white');

  const [validated, setValidated] = useState(false);
  const [isImageValid, setIsImageValid] = useState(true);
  const [newPDF, setNewPDF] = useState(false);
  const [existingPDF, setExistingPDF] = useState(false);
  const [discountDetail, setDiscountDetail] = useState({
    name: "",
    description: "",
    address: "",
    mode: "percentage",
    discount: "",
    logo: null,
    categories: [],
    is_active: true,
    coordinates: "",
    instagram: "",
    facebook: "",
    telephone: "",
    website: "",
    code: "",
    type: "single",
    pdf: "",
  });

  useEffect(() => {
    fetchSingleDiscount();

    return () => {
      setDiscountDetail({
        name: "",
        description: "",
        mode: "percentage",
        discount: "",
        address: "",
        logo: null,
        categories: [],
        is_active: true,
        coordinates: "",
        instagram: "",
        facebook: "",
        telephone: "",
        website: "",
        code: "",
        type: "single",
        pdf: ""
      })
    }
  }, [params.id]);

  const fetchSingleDiscount = async () => {
    if (params.id) {
      const discount = await getSingleDiscount(params.id);
      if (discount) {
        setDiscountDetail({ ...discount, code: discount.code === null ? '' : discount.code });
        setExistingPDF(discount.pdf)
      }
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setValidated(true);

    if (
      !discountDetail.name ||
      !discountDetail.discount ||
      !discountDetail.type ||
      !discountDetail.mode ||
      (discountDetail.mode === 'percentage' && Number(discountDetail.discount) > 100) ||
      (discountDetail.mode === 'amount' && Number(discountDetail.discount) < 0) ||
      (discountDetail.code !== null && discountDetail.code.length > 50) ||
      discountDetail.categories.length === 0 ||
      (discountDetail.facebook && !isValidUrl(discountDetail.facebook)) ||
      (discountDetail.instagram && !isValidUrl(discountDetail.instagram)) ||
      (discountDetail.website && !isValidUrl(discountDetail.website)) ||
      (discountDetail.telephone && (discountDetail.telephone.length < 8 || discountDetail.telephone.length > 11)) ||
      !isImageValid
    ) {
      return;
    }

    const selectedCategories = discountDetail.categories.length > 0 ? discountDetail.categories.map(category => category.value) : []

    try {
      const formData = new FormData();
      formData.append('name', discountDetail.name);
      formData.append('description', discountDetail.description);
      formData.append('address', discountDetail.address);
      formData.append('mode', discountDetail.mode);
      formData.append('discount', discountDetail.discount);
      formData.append('categories', selectedCategories);
      formData.append('logo', discountDetail.logo ? discountDetail.logo : '');
      formData.append('is_active', discountDetail.is_active ? 1 : 0);
      formData.append('coordinates', discountDetail.coordinates ? discountDetail.coordinates : '');
      formData.append('instagram', discountDetail.instagram ? discountDetail.instagram.trim() : null);
      formData.append('facebook', discountDetail.facebook ? discountDetail.facebook.trim() : null);
      formData.append('telephone', discountDetail.telephone ? discountDetail.telephone.trim() : null);
      formData.append('website', discountDetail.website ? discountDetail.website.trim() : null);
      formData.append('code', discountDetail.code ? discountDetail.code : '');
      formData.append('type', discountDetail.type ? discountDetail.type : '');
      if (newPDF) {
        formData.append('pdf', newPDF);
      } else if (existingPDF) {
        formData.append('pdf', existingPDF);
      } else {
        formData.append('pdf', null);
      }

      if (params.id) {
        const data = await updateDiscount(params.id, formData);
        if (!data.error) {
          navigate('/admin/discounts');
        }
      } else {
        const data = await createDiscount(formData);
        if (!data.error) {
          navigate('/admin/discounts');
        }
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleRemovePDF = () => {
    setExistingPDF(null)
  }

  return (
    <Flex
      direction="column"
      minH="100vh"
      align="start"
      top="0"
      pt={{ sm: '125px', lg: '75px' }}
      position="relative"
      w="100%"
    >
      <Tabs w="100%">
        <TabPanels mt="24px" maxW="100%" w="100%" mx="auto">
          <TabPanel w="100%" p="0px" mx="auto">
            <Text color={textColor} fontSize="2xl" fontWeight="700" mb="20px">
              Discount Info
            </Text>
            <form onSubmit={handleSubmit}>
              <Flex direction="column" w="100%">
                <Stack direction="column" spacing="20px">
                  <SimpleGrid
                    columns={{ base: 1, md: 2 }}
                    gap="20px"
                    w="100%"
                    templateColumns={{ md: "1fr 1fr" }}
                  >
                    <Stack spacing="20px">
                      <CInput
                        label="Name of shop"
                        type="text"
                        value={discountDetail.name}
                        onChange={(value) => setDiscountDetail({ ...discountDetail, name: value })}
                        required={true}
                        validated={validated}
                        placeholder="e.g., Esthera"
                        w="100%"
                      />
                      <CInput
                        label="Location"
                        type="textarea"
                        value={discountDetail.address}
                        onChange={(value) => setDiscountDetail({ ...discountDetail, address: value })}
                        required={true}
                        validated={validated}
                        placeholder="Enter Address"
                        w="100%"
                      />
                      <DiscountCategoryDropdown
                        value={discountDetail.categories}
                        onChange={(value) => setDiscountDetail({ ...discountDetail, categories: value })}
                        required={true}
                        multiSelect={true}
                        error={discountDetail.categories.length === 0 && validated}
                      />
                      <CRadio
                        label="Discount Mode"
                        options={discountModeOptions}
                        value={discountDetail.mode}
                        onChange={(value) => setDiscountDetail({ ...discountDetail, mode: value })}
                        required={true}
                        isInvalid={validated && !discountDetail.mode}
                        errorMessage="Please select a Discount Mode."
                      />
                      <CRadio
                        label="Discount Type"
                        options={discountOptions}
                        value={discountDetail.type}
                        onChange={(value) => setDiscountDetail({ ...discountDetail, type: value })}
                        required={true}
                        isInvalid={validated && !discountDetail.type}
                        errorMessage="Please select a Discount Type."
                      />
                      <CInput
                        label="Discount"
                        type="number"
                        value={discountDetail.discount}
                        onChange={(value) => setDiscountDetail({ ...discountDetail, discount: value })}
                        required={true}
                        validated={validated}
                        maxDecimals={2}
                        minValue={0}
                        maxValue={discountDetail.mode === 'percentage' ? 100 : null}
                        symbol={discountDetail.mode === 'percentage' ? '%' : '€'}
                        w="100%"
                      />
                      <CInput
                        label="Discount code"
                        type="text"
                        value={discountDetail.code}
                        onChange={(value) => setDiscountDetail({ ...discountDetail, code: value })}
                        validated={validated}
                        placeholder="NEWFFG"
                        validationRules={{ maxLength: 50 }}
                        w="100%"
                      />
                      <CInput
                        label="Active"
                        type="switch"
                        colorScheme="orange"
                        value={discountDetail.is_active}
                        onChange={(value) =>
                          setDiscountDetail({
                            ...discountDetail,
                            is_active: value,
                          })
                        }
                        validated={validated}
                        w="100%"
                      />
                      <CSingleFileUpload
                        label="Upload PDF"
                        fileType="pdf"
                        onChange={(files) => setNewPDF(files)}
                        required={true}
                        validated={validated}
                        setValidated={setValidated}
                        multiple={true}
                      />
                      {
                        existingPDF &&
                        (
                          <FormControl display='flex'>
                            {/* <Button
                              onClick={() => window.open(existingPDF, '_blank')}
                              style={{
                                background: 'none',
                                border: 'none',
                                padding: 0,
                                color: 'blue',
                                textDecoration: 'underline',
                                cursor: 'pointer',
                                fontSize: 'l',
                                alignSelf: 'start'

                              }}
                            >
                              View PDF
                            </Button> */}
                            {/* <Badge colorScheme={'blue'} cursor='pointer' fontSize="sm" fontWeight="500" onClick={() => window.open(existingPDF, '_blank')}>
                              View PDF
                            </Badge> */}
                            <Icon
                              as={ImFilePdf}
                              h={20}
                              w={20}
                              onClick={() => window.open(existingPDF, '_blank')} cursor="pointer"
                              title='View PDF'
                              color='red'
                            />
                            {
                              existingPDF && typeof existingPDF === 'string' && (
                                <Icon
                                  as={IoCloseCircleOutline}
                                  h={5}
                                  w={5}
                                  onClick={() => handleRemovePDF()}
                                  cursor="pointer"
                                  title='Remove File'
                                  color='red'
                                />
                              )
                            }
                          </FormControl>
                        )
                      }
                      <ImageUpload
                        label="Logo"
                        existingImage={discountDetail.logo && discountDetail.logo instanceof File ? URL.createObjectURL(discountDetail.logo) : discountDetail.logo}
                        onImageSelect={(file) =>
                          setDiscountDetail({ ...discountDetail, logo: file })
                        }
                        validateDimensions={true}
                        setIsImageValid={setIsImageValid}
                        width={200}
                        height={200}
                      />
                    </Stack>
                    <FormControl>
                      <Box mb="20px">
                        <FormLabel
                          display="flex"
                          ms="4px"
                          fontSize="l"
                          fontWeight="900"
                          color={textColor}
                          mb="8px"
                        >
                          Description
                        </FormLabel>
                        <Editor
                          value={discountDetail.description}
                          onTextChange={(e) =>
                            setDiscountDetail({ ...discountDetail, description: e.htmlValue })
                          }
                          style={{ height: '195px' }}
                        />
                      </Box>
                      <CInput
                        label="Coordinates"
                        type="text"
                        value={discountDetail.coordinates}
                        onChange={(value) => setDiscountDetail({ ...discountDetail, coordinates: value })}
                        validated={validated}
                        tooltipContent={{
                          header: "Input as per below format.",
                          content: "37.12341234,37.213432;12.2341234,23.321441",
                        }}
                        symbol={<FiMapPin />}
                        placeholder="12.202556,20.256688;24.526585, 12.232566"
                        w="100%"
                      />
                      <CInput
                        label="Facebook"
                        type="text"
                        value={discountDetail.facebook}
                        onChange={(value) => setDiscountDetail({ ...discountDetail, facebook: value })}
                        validated={validated}
                        validationRules={{ url: true }}
                        placeholder="Facebook URL"
                        symbol={<FaFacebook />}
                        w="100%"
                      />
                      <CInput
                        label="Instagram"
                        type="text"
                        value={discountDetail.instagram}
                        onChange={(value) => setDiscountDetail({ ...discountDetail, instagram: value })}
                        validated={validated}
                        validationRules={{ url: true }}
                        placeholder="Instagram URL"
                        symbol={<FaInstagram />}
                        w="100%"
                      />
                      <CInput
                        label="Website"
                        type="text"
                        value={discountDetail.website}
                        onChange={(value) => setDiscountDetail({ ...discountDetail, website: value })}
                        validated={validated}
                        validationRules={{ url: true }}
                        symbol={<TbWorldWww />}
                        placeholder="Website URL"
                        w="100%"
                      />
                      <CInput
                        label="Telephone"
                        type="number"
                        validationRules={{ minLength: 8, maxLength: 11 }} // Malta phone numbers are 8 digits long locally and 11 digits with the country code (+356).
                        value={discountDetail.telephone}
                        onChange={(value) => setDiscountDetail({ ...discountDetail, telephone: value })}
                        validated={validated}
                        placeholder="Enter telephone number."
                        symbol={<BsFillTelephoneFill />}
                        w="100%"
                      />
                    </FormControl>
                  </SimpleGrid>
                </Stack>

                <Flex justify="flex-end" align="center" mt="44px" gap="3">
                  <Button
                    variant="darkBrand"
                    fontSize="sm"
                    borderRadius="10px"
                    w={{ base: '128px', md: '148px' }}
                    h="46px"
                    type="button"
                    bg="gray"
                    color="white"
                    _hover={{ bg: 'gray.600' }}
                    onClick={() => navigate('/admin/discounts')}
                  >
                    Cancel
                  </Button>
                  <CButton
                    w={{ base: '128px', md: '148px' }}
                    h="46px"
                    type="submit"
                  >
                    Submit
                  </CButton>
                </Flex>
              </Flex>
            </form>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Flex>
  );
}